import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Check, Loader2, Send } from 'lucide-react';
import { useState } from 'react';
import { PodgladPliku } from '@/components/PodgladPliku';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, FormGrid } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { listaObrazkow, przypiszObrazekZBiblioteki, type Pokrycie } from '@/lib/items-api';
import { maszynyZPlikiem, type MaszynaZrodlowa } from '@/lib/zrodla-obrazka';

/**
 * ŚCIĄGNIJ I ROZGŁOŚ — naprawa wiersza, w którym portal NIE MA pliku, a maszyny go mają.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 07.09.2026 PO ZRZUCIE Z TESTÓWKI PESA, na którym stało „braki: 13", a przy
 *  każdym z tych wierszy „portal nie ma tego pliku". TE WIERSZE SĄ NIENAPRAWIALNE PUSHEM: nie ma
 *  po naszej stronie ani jednego bajtu, więc nie ma czego wysłać. Jedyna droga prowadzi w drugą
 *  stronę — ściągnąć z maszyny, która plik ma, i dopiero wtedy rozesłać na pozostałe.
 *
 *  ⚠️⚠️ TRZY KROKI, NIE JEDEN PRZYCISK, i to jest rozstrzygnięte: „nie rozsyłaj od razu".
 *  Pomiędzy pobraniem a rozesłaniem MUSI zmieścić się spojrzenie na obrazek, bo pod jedną nazwą
 *  leżą na automatach RÓŻNE pliki i to jest cała przyczyna, dla której ta funkcja istnieje.
 *  Jeden przycisk „napraw" wybierałby za człowieka, która wersja jest prawdziwa.
 *
 *  ⚠️ ROZESŁANIE IDZIE ISTNIEJĄCĄ TRASĄ (`przypiszObrazekZBiblioteki`), która przypisuje plik
 *  towarowi i wysyła go na WSZYSTKIE automaty wyspy. Nie budujemy „wyślij tylko na te, którym
 *  brakuje": druga trasa zapisu do tych samych tabel to druga reguła nadpisywania, a różnica
 *  między nimi wyszłaby dopiero na maszynie klienta.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
type Wiersz = Pokrycie['pokrycie'][number];

export function NaprawObrazek({
  groupId,
  wiersz,
  maszyny,
  onClose,
}: {
  groupId: string;
  wiersz: Wiersz;
  maszyny: Pokrycie['maszyny'];
  onClose: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [zrodlo, setZrodlo] = useState<MaszynaZrodlowa | null>(null);
  const [wBibliotece, setWBibliotece] = useState<string | null>(null);
  const [blad, setBlad] = useState<string | null>(null);

  /**
   * ⚠️ NAZWY Z BIBLIOTEKI SĄ POTRZEBNE DO PODPOWIEDZI NAZWY przy zapisie: bez nich okno nie wie,
   * czy nazwa jest wolna, i albo zawsze dokleja węzeł, albo zawsze nadpisuje. Jedno zapytanie
   * o pierwszą setkę wystarcza — kolizję i tak rozstrzyga API przy zapisie.
   */
  const biblioteka = useQuery({
    queryKey: ['obrazki', groupId, '', 100],
    queryFn: () => listaObrazkow(groupId, '', 100),
  });
  const nazwyWBibliotece = (biblioteka.data?.pliki ?? []).map((p) => p.nazwaPliku);

  /**
   * ⚠️⚠️ KANDYDACI TO TYLKO MASZYNY, KTÓRE PLIK MAJĄ. Wypisanie wszystkich znaczyłoby, że człowiek
   * wybiera węzeł, z którego nie ma czego ściągnąć — a zadanie skończyłoby się czerwonym wpisem
   * w historii maszyny za nic. Reguła stoi w `zrodla-obrazka.ts`.
   */
  const kandydaci = maszynyZPlikiem(wiersz, maszyny);

  const rozglos = useMutation({
    mutationFn: (nazwaPliku: string) => przypiszObrazekZBiblioteki(wiersz.towar.id, { groupId, nazwaPliku }),
    onSuccess: (r) => {
      toast.success(t('napraw.rozeslano').replace('{n}', String(r.automatow)).replace('{plik}', r.nazwaPliku));
      void qc.invalidateQueries({ queryKey: ['pokrycie-obrazkow', groupId] });
      void qc.invalidateQueries({ queryKey: ['items'] });
      onClose();
    },
    onError: (e: Error) => setBlad(e instanceof ApiError ? e.message : t('common.error')),
  });

  const plik = wiersz.towar.imageFile ?? '';

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="w-[min(46rem,calc(100vw-2rem))]"
        title={t('napraw.tytul')}
        description={`${wiersz.towar.nazwa} · ${wiersz.towar.itemNumber} · ${plik}`}
      >
        <FormGrid>
          {blad !== null && (
            <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
              <span className="min-w-0 break-words">{blad}</span>
            </p>
          )}

          {/* ══════ KROK 1: SKĄD ══════ */}
          {zrodlo === null && (
            <div className="space-y-2">
              <p className="text-sm leading-snug">{t('napraw.krok1')}</p>
              {kandydaci.length === 0 ? (
                /**
                 * ⚠️⚠️ MÓWIMY WPROST, ŻE NIE MA SKĄD WZIĄĆ. Ten wiersz nie da się naprawić z portalu:
                 * pliku nie ma ani u nas, ani na żadnej ZMIERZONEJ maszynie. Puste okno kazałoby
                 * szukać przycisku, którego nie ma.
                 */
                <p className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs leading-snug">
                  {t('napraw.brakZrodla')}
                </p>
              ) : (
                <ul className="space-y-1">
                  {kandydaci.map((m) => (
                    <li key={m.agentId}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-2 rounded-md border border-border p-2 text-left text-xs hover:bg-accent/50"
                        onClick={() => setZrodlo(m)}
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{m.nazwa}</span>
                          {/*
                            ⚠️ NAZWA PLIKU I ROZMIAR PRZY KAŻDYM KANDYDACIE — po nich widać, że wersje
                            SIĘ RÓŻNIĄ, jeszcze przed pobraniem. To jedyna wskazówka, z którego
                            automatu warto brać, gdy plików jest kilka pod jedną nazwą.
                          */}
                          <span className="block font-mono text-[10px] text-muted-foreground">
                            {m.plik}
                            {m.rozmiar !== null && ` · ${Math.round(m.rozmiar / 1024)} kB`}
                          </span>
                        </span>
                        <span className="shrink-0 text-[11px] text-[var(--primary)]">{t('napraw.wezZtej')}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* ══════ KROK 2: OBEJRZYJ I ZAPISZ ══════ */}
          {zrodlo !== null && wBibliotece === null && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm leading-snug">{t('napraw.krok2').replace('{maszyna}', zrodlo.nazwa)}</p>
                <Button type="button" size="sm" variant="ghost" onClick={() => setZrodlo(null)}>
                  <ArrowLeft className="size-3.5" />
                  {t('napraw.innaMaszyna')}
                </Button>
              </div>
              <PodgladPliku
                groupId={groupId}
                agentId={zrodlo.agentId}
                nodeId={zrodlo.nodeId}
                /** ⚠️ PYTAMY O PLIK, KTÓRY WIDZI TA MASZYNA — po to, żeby zobaczyć, co tam NAPRAWDĘ leży. */
                nazwaPliku={zrodlo.plik}
                wBibliotece={nazwyWBibliotece}
                onZapisano={setWBibliotece}
              />
            </div>
          )}

          {/* ══════ KROK 3: ROZGŁOŚ ══════ */}
          {wBibliotece !== null && (
            <div className="space-y-2">
              <p className="flex items-start gap-2 rounded-md border border-[var(--success)]/40 bg-[var(--success)]/10 p-2 text-xs leading-snug">
                <Check className="mt-0.5 size-3.5 shrink-0 text-[var(--success)]" />
                <span>{t('napraw.wBibliotece').replace('{plik}', wBibliotece)}</span>
              </p>
              {/*
                ⚠️⚠️ MÓWIMY, ILU AUTOMATÓW TO DOTKNIE I ŻE NADPISZE. Trasa przypisania z biblioteki
                wysyła plik na WSZYSTKIE automaty wyspy — także te, które mają pod tą nazwą inny
                obrazek. Przy tej funkcji to jest właśnie zamierzone (ujednolicenie), ale musi być
                powiedziane, bo drugiej wersji już potem nie ma skąd wziąć.
              */}
              <p className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs leading-snug">
                {t('napraw.rozeslijOstrzezenie')
                  .replace('{n}', String(maszyny.length))
                  .replace('{plik}', wBibliotece)}
              </p>
              <Button type="button" disabled={rozglos.isPending} onClick={() => rozglos.mutate(wBibliotece)}>
                {rozglos.isPending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                {t('napraw.rozeslij')}
              </Button>
              {/*
                ⚠️ WYJŚCIE BEZ ROZSYŁANIA MUSI ISTNIEĆ: plik jest już w bibliotece i to samo w sobie
                jest wynikiem. Człowiek może chcieć najpierw ściągnąć wersje z pozostałych automatów
                i je porównać — po to rozdzieliliśmy te kroki.
              */}
              <p className="text-[11px] text-muted-foreground">{t('napraw.mozeszWyjsc')}</p>
            </div>
          )}
        </FormGrid>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
